'use client'

import { EaPlayerStats, EaMatch } from 'lib/proclubs/shared'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'ui/table'
import { Button } from 'ui/button'
import AvatarCustom from 'components/AvatarCustom'
import { Eye } from 'lucide-react'
import { convertFullNameToPositionCode } from 'utils/formations'

interface ProclubsTeamLineupProps {
    clubId: string
    clubName: string
    rawMatch: EaMatch
    onPlayerClick: (player: EaPlayerStats) => void
}

export const ProclubsTeamLineup = ({
    clubId,
    clubName,
    rawMatch,
    onPlayerClick,
}: ProclubsTeamLineupProps) => {
    if (!rawMatch.players || !rawMatch.players[clubId]) {
        return <div className='text-base text-center'>No lineup data available</div>
    }

    const clubPlayers = rawMatch.players[clubId]
    const players = Object.values(clubPlayers)

    if (players.length === 0) {
        return <div className='text-base text-center'>No players in lineup</div>
    }

    // Helper to get numeric value or default to 0
    const getValue = (val: string | undefined | null): string => {
        if (!val || val === '' || val === '-') return '0'
        return val
    }

    // Helper to get numeric value with 1 decimal place for rating
    const getRating = (val: string | undefined | null): string => {
        if (!val || val === '' || val === '-') return '0.0'
        const num = parseFloat(val)
        if (isNaN(num)) return '0.0'
        return num.toFixed(1)
    }

    // Convert position to short form (ST, CM, CB, GK, etc.)
    const getPositionShort = (pos: string | undefined | null): string => {
        if (!pos || pos === '' || pos === '-') return 'N/A'
        // If already short (2-3 uppercase letters), return as is
        if (pos.length <= 3 && pos === pos.toUpperCase()) {
            return pos
        }
        // Try to convert full name to short code using the utility
        const shortCode = convertFullNameToPositionCode(pos)
        if (shortCode) {
            return shortCode
        }
        // Fallback: return first 3 uppercase letters if conversion fails
        return pos.toUpperCase().substring(0, 3)
    }

    return (
        <div className='flex flex-col gap-2'>
            <h3 className='text-lg font-semibold'>{clubName}</h3>
            <div className='overflow-x-auto'>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className='w-[60px]'>Logo</TableHead>
                            <TableHead className='min-w-[150px]'>Name</TableHead>
                            <TableHead className='w-[80px] text-center'>Position</TableHead>
                            <TableHead className='w-[80px] text-center'>Goals</TableHead>
                            <TableHead className='w-[80px] text-center'>Assists</TableHead>
                            <TableHead className='w-[80px] text-center'>Rating</TableHead>
                            <TableHead className='w-[100px] text-center'>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {players.map((player, index) => {
                            const playerName = player.playername || 'Unknown Player'
                            const position = getPositionShort(player.pos)
                            const goals = getValue(player.goals)
                            const assists = getValue(player.assists)
                            const rating = getRating(player.rating)

                            return (
                                <TableRow key={`${clubId}-${player.playername}-${index}`}>
                                    <TableCell>
                                        <AvatarCustom
                                            size='small'
                                            className='border border-white'
                                            avatarURL={null}
                                        >
                                            {playerName}
                                        </AvatarCustom>
                                    </TableCell>
                                    <TableCell className='font-medium'>{playerName}</TableCell>
                                    <TableCell className='text-center'>{position}</TableCell>
                                    <TableCell className='text-center'>{goals}</TableCell>
                                    <TableCell className='text-center'>{assists}</TableCell>
                                    <TableCell className='text-center'>{rating}</TableCell>
                                    <TableCell className='text-center'>
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={() => onPlayerClick(player)}
                                            className='gap-1'
                                        >
                                            <Eye className='h-4 w-4' />
                                            View Stats
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}

